/* eslint-disable max-len */
/* eslint-disable react/no-danger */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Text from '../../../../shared/components/Text/Text';
import Spinner from '../../../../shared/components/Spinner/Spinner';
import { setIframelyError, uploadMediaUrl } from '../../ducks/calendarActions';

const ErrorContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 15px;
  gap: 15px;
`;

const Iframely = ({
  url, setFieldValue, videoUrlValue, mediaType,
}) => {
  const dispatch = useDispatch();
  const iframelyError = useSelector((state) => state.calendar.data.iframelyError);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountCode = currentUser?.accountCode;

  const [isLoading, setIsLoading] = useState(false);
  const [html, setHtml] = useState(null);

  const KEY = process.env.REACT_APP_IFRAMELY_KEY;

  /** Remove web-share permission & fix iframe styling */
  const sanitizeIframeHtml = (responseHtml) => responseHtml
    .replace(/allow="[^"]*web-share[^"]*"/g, 'allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"')
    .replace(/height:\s*0;/g, 'height: 100%;')
    .replace(/<iframe/g, '<iframe style="top:0; left:0; width:100%; height:100%; position:absolute; border:0; border-radius:4px;"');

  /** Check if the HTML contains an iframe or video element */
  const isIframeOrVideo = (htmlContent) => /<iframe[^>]*>/i.test(htmlContent) || /<video[^>]*>/i.test(htmlContent);

  useEffect(() => {
    if (!url) return;
    setIsLoading(true);
    setHtml(null);

    /** If we already have stored media, skip Iframely */
    if (videoUrlValue && (mediaType === 'EMBED_LINK' || mediaType === 'VIDEO')) {
      setTimeout(() => {
        setIsLoading(false);
      }, 150);
      return;
    }

    setTimeout(() => {
      axios
        .get(`https://iframe.ly/api/iframely?url=${encodeURIComponent(url)}&key=${KEY}`)
        .then((res) => {
          dispatch(setIframelyError(null));

          /** Find iframe player */
          const playerLink = res.data.links?.player?.find(
            (video) => video?.type === 'text/html' && video?.href,
          );
          if (playerLink) {
            const embedLink = playerLink.href;
            /** Iframely iframe */
            const generatedHtml = sanitizeIframeHtml(res.data.html);

            if (isIframeOrVideo(generatedHtml)) {
              dispatch(
                uploadMediaUrl(embedLink, accountCode, {
                  onSuccess: (response) => {
                    setIsLoading(false);
                    setHtml({ __html: generatedHtml });
                    setFieldValue('mediaId', response.id);
                  },
                  onError: () => {
                    setIsLoading(false);
                    dispatch(
                      setIframelyError({ message: 'Unable to upload embed link to database.' }),
                    );
                  },
                }),
              );
            } else {
              setIsLoading(false);
              dispatch(setIframelyError({ message: 'The video cannot be embedded.' }));
            }
          } else {
          /** No embeddable player link → show error */
            setIsLoading(false);
            dispatch(setIframelyError({ message: 'The video cannot be embedded.' }));
          }
        })
        .catch(() => {
          setIsLoading(false);
          dispatch(setIframelyError({ message: 'Please upload a valid video URL.' }));
        });
    }, 500);
  }, [url]);

  if (iframelyError && !isLoading && !html) {
    return (
      <ErrorContainer>
        <IcomoonReact iconSet={iconSet} size={50} icon='warning' color='#E4E4E4' />
        <Text color='#9E9E9E' style={{ overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal' }}>
          {iframelyError.message.length > 200
            ? `${iframelyError.message.substring(0, 200)}...`
            : iframelyError.message}
        </Text>
      </ErrorContainer>
    );
  }

  if (isLoading) return <Spinner />;
  if (videoUrlValue && mediaType === 'VIDEO') {
    return (
      <video
        className='media_player'
        controls
        autoPlay
        controlsList='nodownload'
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      >
        <source src={videoUrlValue} type='video/mp4' />
        <track
          kind='captions'
          src='/captions.vtt'
          srcLang='en'
          label='English'
          default
        />
      </video>
    );
  }
  if (videoUrlValue && mediaType === 'EMBED_LINK') {
    return (
      <iframe style={{ display: 'flex', width: '100%', height: '100%' }} title='media_player' allowFullScreen src={videoUrlValue} />
    );
  }

  return <div style={{ display: 'flex', width: '100%', height: '100%' }} dangerouslySetInnerHTML={html} />;
};

export default Iframely;
